import { useCallback, useMemo } from 'react';
import { useClient } from 'urql';
import { fromPromise } from 'xstate5';
import { CaloriesVersion, CostChannel } from '@sg/graphql-schema';

import {
  CustomizationDataDocument,
  type CustomizationDataQuery,
  type CustomizationDataQueryVariables,
  CustomizationEditDataDocument,
  type CustomizationEditDataQuery,
  type CustomizationEditDataQueryVariables,
} from '../../../graphql/CustomizationData.generated';
import { customizationLogger } from '../../helpers';
import {
  type CustomizationDataInput,
  type CustomizationDataResponse,
  type EditCustomizationDataInput,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

export const useFetchCustomizationDataActor = () => {
  const client = useClient();

  // ─────────────────────────────────────────────────────────────────────

  const fetchCustomizationData = useCallback(
    async (params: CustomizationDataInput) => {
      const {
        productSlug,
        restaurantSlug,
        ingredientAssetVersion,
        isDelivery,
      } = params;

      const variables: CustomizationDataQueryVariables = {
        productId: productSlug,
        restaurantId: restaurantSlug,
        ingredientAssetVersion,
        costChannel: isDelivery
          ? CostChannel.DeliveryCostChannel
          : CostChannel.DefaultCostChannel,
        caloriesVersion: CaloriesVersion.V2,
      };

      // ─────────────────────────────────────────────────────────────────────

      const query = client.query<CustomizationDataQuery, CustomizationDataQueryVariables>; // prettier-ignore
      const request = query(CustomizationDataDocument, variables, { requestPolicy: 'network-only' }).toPromise; // prettier-ignore

      const { data, error } = await request();

      const hasFetchedProductData = data?.product.__typename === 'Product';
      const hasFetchedRestaurantData = data?.restaurant?.__typename === 'Restaurant'; // prettier-ignore

      // ─── Error ───────────────────────────────────

      if (!hasFetchedProductData || !hasFetchedRestaurantData) {
        customizationLogger.error(
          `Failed to fetch customization data for product "${productSlug}".`,
          error ? { networkError: error } : undefined,
        );

        throw new Error('Failed to fetch customization data');
      }

      // ─── Success ─────────────────────────────────

      return {
        product: data.product,
        restaurant: data.restaurant,
      };
    },
    [client.query],
  );

  const fetchEditCustomizationData = useCallback(
    async (params: EditCustomizationDataInput) => {
      const {
        productSlug,
        restaurantSlug,
        ingredientAssetVersion,
        lineItemId,
        isDelivery,
      } = params;

      const variables: CustomizationEditDataQueryVariables = {
        productId: productSlug,
        restaurantId: restaurantSlug,
        ingredientAssetVersion,
        costChannel: isDelivery
          ? CostChannel.DeliveryCostChannel
          : CostChannel.DefaultCostChannel,
        caloriesVersion: CaloriesVersion.V2,
        lineItemInput: { id: lineItemId },
      };

      // ─────────────────────────────────────────────────────────────────────

      const query = client.query<CustomizationEditDataQuery, CustomizationEditDataQueryVariables>; // prettier-ignore
      const request = query(CustomizationEditDataDocument, variables, { requestPolicy: 'network-only' }).toPromise; // prettier-ignore

      const { data, error } = await request();

      const hasFetchedProductData = data?.product.__typename === 'Product';
      const hasFetchedRestaurantData = data?.restaurant?.__typename === 'Restaurant'; // prettier-ignore
      const hasFetchedLineItemData = data?.lineItem?.__typename === 'LineItemSuccess'; // prettier-ignore

      // ─── Error ───────────────────────────────────

      if (
        !hasFetchedProductData ||
        !hasFetchedRestaurantData ||
        !hasFetchedLineItemData
      ) {
        customizationLogger.error(
          `Failed to fetch customization data for product "${productSlug}".`,
          error ? { networkError: error } : undefined,
        );

        throw new Error('Failed to fetch customization data');
      }

      // ─── Success ─────────────────────────────────

      return {
        product: data.product,
        restaurant: data.restaurant,
        lineItem: data.lineItem.lineItem,
      };
    },
    [client.query],
  );

  const updateCustomerPromise = useCallback(
    async (
      input: CustomizationDataInput,
    ): Promise<CustomizationDataResponse> => {
      const { lineItemId } = input;

      // ─── Modified Product ────────────────────────────────────────────────

      if (lineItemId) {
        return fetchEditCustomizationData({ ...input, lineItemId });
      }

      // ─────────────────────────────────────────────────────────────────────

      return fetchCustomizationData(input);
    },
    [fetchCustomizationData, fetchEditCustomizationData],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    return fromPromise<CustomizationDataResponse, CustomizationDataInput>(
      async (args) => {
        return updateCustomerPromise(args.input);
      },
    );
  }, [updateCustomerPromise]);
};
